import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Bold, Italic, List, ListOrdered, Link2 } from 'lucide-react';

// Merge fields available in a template body and subject line — mirrors
// packages/backend/src/routes/emailTemplates.ts SAMPLE_MERGE_VALUES.
export const MERGE_FIELDS = ['first_name', 'last_name', 'team_name', 'sport', 'school_name', 'school_city', 'team_store_url', 'store_banner_url', 'campaign_image_url'] as const;

interface BodyEditorProps {
  value: string;
  onChange: (html: string) => void;
}

/**
 * Constrained rich-text editor for an email template body. Only paragraphs,
 * bold, italic, bullet/ordered lists, and links are allowed — no headings,
 * blockquotes, code, strikethrough, or horizontal rules — so every template
 * renders predictably inside the fixed HTML email shape.
 *
 * The installed TipTap major version folds the Link mark into StarterKit
 * itself (it was a standalone extension in earlier majors), so link
 * behavior is configured via `StarterKit.configure({ link: {...} })`
 * instead of also registering `@tiptap/extension-link` — adding both would
 * register the same mark name twice. That version's StarterKit also adds
 * an `underline` mark that earlier majors didn't include; it is disabled
 * here to keep the constraint set (and the toolbar) unchanged.
 */
export function BodyEditor({ value, onChange }: BodyEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        blockquote: false,
        code: false,
        codeBlock: false,
        horizontalRule: false,
        strike: false,
        underline: false,
        link: { openOnClick: false, autolink: true },
      }),
    ],
    content: value,
    onUpdate: ({ editor: e }) => onChange(e.getHTML()),
    editorProps: {
      // No Tailwind typography plugin is installed in this project, so the
      // body relies on the house `.review-doc` component class (already
      // used for admin-authored HTML in ReviewPage.tsx) for list/link
      // styling instead of `prose`.
      attributes: {
        class:
          'review-doc text-sm text-gray-800 dark:text-gray-200 max-w-none min-h-[10rem] px-3 py-2 focus:outline-none',
      },
    },
  });
  if (!editor) return null;

  const btn = (active: boolean) =>
    `p-1.5 rounded ${active ? 'bg-blast-100 text-blast-700 dark:bg-blast-900/40 dark:text-blast-300' : 'text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'}`;

  // Toolbar controls sit outside the editor's contenteditable, so a plain
  // click first fires a mousedown that the browser uses to move DOM focus
  // (and the text selection) to the control being clicked. That happens
  // before the click handler below runs, so by the time
  // `editor.chain().focus()...` executes, the editor's selection has
  // already been displaced from the line the user meant to act on.
  // Blocking the mousedown's default action keeps focus/selection in the
  // editor throughout, so the command below always applies to the
  // selection the user actually made.
  const preserveSelection = (e: React.MouseEvent) => e.preventDefault();

  const setLink = () => {
    const previous = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('Link URL (https://…)', previous ?? 'https://');
    if (url === null) return;
    if (url === '') editor.chain().focus().unsetLink().run();
    else editor.chain().focus().setLink({ href: url }).run();
  };

  return (
    <div className="border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700">
      <div className="flex flex-wrap items-center gap-1 border-b border-gray-200 dark:border-gray-600 px-2 py-1.5">
        <button type="button" onMouseDown={preserveSelection} className={btn(editor.isActive('bold'))} onClick={() => editor.chain().focus().toggleBold().run()} aria-label="Bold"><Bold className="w-4 h-4" /></button>
        <button type="button" onMouseDown={preserveSelection} className={btn(editor.isActive('italic'))} onClick={() => editor.chain().focus().toggleItalic().run()} aria-label="Italic"><Italic className="w-4 h-4" /></button>
        <button type="button" onMouseDown={preserveSelection} className={btn(editor.isActive('bulletList'))} onClick={() => editor.chain().focus().toggleBulletList().run()} aria-label="Bulleted list"><List className="w-4 h-4" /></button>
        <button type="button" onMouseDown={preserveSelection} className={btn(editor.isActive('orderedList'))} onClick={() => editor.chain().focus().toggleOrderedList().run()} aria-label="Numbered list"><ListOrdered className="w-4 h-4" /></button>
        <button type="button" onMouseDown={preserveSelection} className={btn(editor.isActive('link'))} onClick={setLink} aria-label="Link"><Link2 className="w-4 h-4" /></button>
        <select
          // No onMouseDown guard here: unlike a <button>, preventDefault()
          // on a <select>'s mousedown blocks its native dropdown from
          // opening at all in Chrome/Firefox, which would break merge-field
          // insertion outright. The editor's own focus-restore is already
          // correct for a select's change event (unlike a raw mousedown),
          // since the browser only moves focus to the select on mousedown,
          // not the text selection out of the editor's DOM.
          className="ml-auto text-xs border border-gray-300 dark:border-gray-600 rounded px-1 py-0.5 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300"
          value=""
          onChange={(e) => { if (e.target.value) editor.chain().focus().insertContent(`{{${e.target.value}}}`).run(); }}
          aria-label="Insert merge field"
        >
          <option value="">Insert field…</option>
          {MERGE_FIELDS.map((f) => <option key={f} value={f}>{`{{${f}}}`}</option>)}
        </select>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
